import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import { pricingRulesApi } from '../services/pricing-rules-api';
import { agruparMarcas, normalMarca, type MarcaAgrupada } from '../lib/marcas';

/** Mismo patrón que `category-multi-select.tsx`, con las marcas juntadas sin mayúsculas. */
export function BrandMultiSelect({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (brands: string[]) => void;
}) {
  const [marcas, setMarcas] = useState<MarcaAgrupada[]>([]);
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState<'CARGANDO' | 'LISTO' | 'ERROR'>('CARGANDO');

  useEffect(() => {
    let vivo = true;
    pricingRulesApi
      .listBrands(true)
      .then((r) => {
        if (!vivo) return;
        setMarcas(agruparMarcas(r.brands));
        setEstado('LISTO');
      })
      .catch(() => vivo && setEstado('ERROR'));
    return () => {
      vivo = false;
    };
  }, []);

  const elegida = (m: MarcaAgrupada) =>
    selected.some((s) => normalMarca(s) === normalMarca(m.name));
  const filtradas = useMemo(
    () => marcas.filter((m) => normalMarca(m.name).includes(normalMarca(search))),
    [marcas, search]
  );
  const alternar = (m: MarcaAgrupada) =>
    onChange(
      elegida(m)
        ? selected.filter((s) => normalMarca(s) !== normalMarca(m.name))
        : [...selected, m.name]
    );

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((b) => (
            <Badge key={b} variant="secondary" className="gap-1">
              {b}
              <button
                type="button"
                aria-label={`Sacar ${b}`}
                onClick={() => onChange(selected.filter((s) => s !== b))}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Input
        aria-label="Buscar marca"
        placeholder={selected.length ? 'agregar otra marca' : 'todas las marcas'}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="max-h-40 overflow-y-auto rounded-md border">
        {estado === 'CARGANDO' && (
          <div className="p-2 text-xs text-muted-foreground">Cargando marcas…</div>
        )}
        {estado === 'ERROR' && (
          <div className="p-2 text-xs text-destructive">No se pudieron traer las marcas.</div>
        )}
        {estado === 'LISTO' && filtradas.length === 0 && (
          <div className="p-2 text-xs text-muted-foreground">Ninguna marca con ese nombre.</div>
        )}
        {filtradas.map((m) => (
          <button
            key={m.name}
            type="button"
            aria-pressed={elegida(m)}
            onClick={() => alternar(m)}
            className={cn(
              'flex w-full items-center justify-between border-b px-3 py-1.5 text-sm last:border-b-0 hover:bg-muted/50',
              elegida(m) && 'bg-primary/10 font-medium'
            )}
          >
            <span>{m.name}</span>
            <span className="text-xs text-muted-foreground">{formatoCantidad(m.count)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
